import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Search, Printer, CheckCircle2, XCircle } from "lucide-react";
import api, { formatRp, imageUrl, formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import TrackingPanel from "@/components/TrackingPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending_payment: "Menunggu Bayar",
  paid: "Diverifikasi",
  processing: "Diproses",
  shipped: "Dikirim",
  completed: "Selesai",
  cancelled: "Dibatalkan",
};
const STATUSES = Object.keys(STATUS_LABEL);

export function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    const params = { limit: 200 };
    if (tab !== "all") params.status = tab;
    if (search) params.search = search;
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data.items));
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-black mb-4">Pesanan</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E5E5E5] rounded-xl p-1 w-full overflow-x-auto no-scrollbar justify-start">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#FFEDD5] data-[state=active]:text-[#E85D04] rounded-lg whitespace-nowrap">Semua</TabsTrigger>
          {STATUSES.map((s) => (
            <TabsTrigger key={s} value={s} className="data-[state=active]:bg-[#FFEDD5] data-[state=active]:text-[#E85D04] rounded-lg whitespace-nowrap" data-testid={`admin-orders-tab-${s}`}>{STATUS_LABEL[s]}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white border border-[#E5E5E5] rounded-2xl mt-4">
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari no pesanan / pelanggan..." className="pl-9 rounded-xl" data-testid="admin-orders-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                <th className="px-4 py-3">No Pesanan</th>
                <th className="px-4 py-3">Pelanggan</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Pembayaran</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tanggal</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6]">
                  <td className="px-4 py-3"><Link to={`/admin/pesanan/${o.id}`} className="font-mono text-xs text-[#E85D04] font-bold" data-testid={`admin-order-${o.order_number}`}>{o.order_number}</Link></td>
                  <td className="px-4 py-3"><div className="font-semibold">{o.user_name}</div><div className="text-xs text-[#5C5C5C]">{o.user_email}</div></td>
                  <td className="px-4 py-3 font-bold">{formatRp(o.total)}</td>
                  <td className="px-4 py-3 uppercase text-xs">{o.payment_method}</td>
                  <td className="px-4 py-3"><span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-[#FFEDD5] text-[#E85D04]">{STATUS_LABEL[o.status]}</span></td>
                  <td className="px-4 py-3 text-xs text-[#5C5C5C]">{new Date(o.created_at).toLocaleDateString("id-ID")}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-[#5C5C5C]">Tidak ada pesanan</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

export function AdminOrderDetail() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const [status, setStatus] = useState("");
  const [tracking, setTracking] = useState("");
  const [settings, setSettings] = useState(null);
  const [jubelio, setJubelio] = useState(null); // {_active, sandbox, enabled, ...}
  const [jubBusy, setJubBusy] = useState(false);
  const [jubStatus, setJubStatus] = useState(null);
  const [jubLog, setJubLog] = useState(null);
  const [jubLogOpen, setJubLogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  // Phase 4: Tracking
  const [trackingDetail, setTrackingDetail] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  // v1.8 Generate AWB state (Fase 3)
  const [awbConfirmOpen, setAwbConfirmOpen] = useState(false);
  const [awbDryRun, setAwbDryRun] = useState(true);  // SAFETY default
  const [awbResult, setAwbResult] = useState(null);
  // Phase 5: Cancel AWB modal state
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/admin/orders/${orderId}`);
    setO(r.data);
    setStatus(r.data.status);
    setTracking(r.data.tracking_number || "");
  }, [orderId]);

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/admin/settings/jubelio").then((r) => setJubelio(r.data)).catch(() => {});
    api.get(`/admin/orders/${orderId}/jubelio/last-log`).then((r) => setJubLog(r.data)).catch(() => {});
    api.get(`/admin/orders/${orderId}/jubelio/tracking`).then((r) => setTrackingDetail(r.data)).catch(() => {});
  }, [load, orderId]);

  if (!o) return <AdminLayout><div className="p-12 text-center text-[#5C5C5C]">Memuat...</div></AdminLayout>;

  const updateStatus = async () => {
    try {
      await api.put(`/admin/orders/${o.id}/status`, { status, tracking_number: tracking || null });
      toast.success("Status pesanan diperbarui");
      load();
    } catch (e) { notifyError(e); }
  };

  const verifyPayment = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/verify-payment`);
      toast.success("Pembayaran diverifikasi");
      load();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };

  const rejectPayment = async () => {
    if (!rejectReason.trim()) {
      toast.error("Alasan penolakan wajib diisi");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/reject-payment`, { reason: rejectReason.trim() });
      toast.success("Pembayaran ditolak");
      setRejectOpen(false);
      setRejectReason("");
      load();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };

  const printInvoice = () => window.print();

  const isQris = o.payment_method === "qris";

  const jubelioActive = !!jubelio?._active;
  const enableCreateAwb = !!jubelio?.enable_create_awb;
  // v1.8: awb adalah field utama, fallback tracking_number untuk backward-compat
  const hasAwb = !!(o.awb || o.jubelio_shipment_id);

  const fetchJubLog = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${o.id}/jubelio/last-log`);
      setJubLog(data);
    } catch (e) { /* silent — log fetch tidak boleh ganggu UX */ }
  };

  // Phase 4: Tracking — fetch cached + refresh dari Jubelio
  const fetchTracking = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${o.id}/jubelio/tracking`);
      setTrackingDetail(data);
    } catch (e) { /* silent */ }
  };

  const refreshTracking = async () => {
    try {
      await api.post(`/admin/orders/${o.id}/jubelio/tracking/sync`);
      toast.success("Tracking berhasil diperbarui");
      await fetchTracking();
      await load();
    } catch (e) {
      notifyError(e);
    }
  };

  const fetchAuditLogs = async () => {
    setAuditBusy(true);
    try {
      const { data } = await api.get(`/admin/orders/${o.id}/jubelio/audit-logs`, { params: { limit: 20 } });
      setAuditLogs(Array.isArray(data?.items) ? data.items : []);
    } catch (e) { /* silent */ }
    finally { setAuditBusy(false); }
  };

  const copyAwb = async () => {
    const value = o.awb || o.tracking_number;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`AWB "${value}" disalin ke clipboard`);
    } catch {
      // Fallback for non-HTTPS / older browsers
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(`AWB "${value}" disalin`); }
      finally { document.body.removeChild(ta); }
    }
  };

  const openAwbConfirm = () => {
    setAwbDryRun(true);  // selalu reset ke dry_run saat buka modal
    setAwbResult(null);
    setAwbConfirmOpen(true);
  };

  const runGenerateAwb = async () => {
    setJubBusy(true);
    try {
      const { data } = await api.post(
        `/admin/orders/${o.id}/jubelio/create-shipment`,
        { dry_run: awbDryRun },
      );
      setAwbResult(data);
      if (data.dry_run) {
        toast.success("Preview payload tersimpan (dry_run). Belum ada panggilan ke Jubelio.");
      } else {
        toast.success(`AWB berhasil dibuat: ${data.awb || "-"}`);
        setAwbConfirmOpen(false);
      }
      load();
    } catch (e) { notifyError(e); }
    finally { setJubBusy(false); fetchJubLog(); }
  };

  const fetchJubStatus = async () => {
    setJubBusy(true);
    try {
      const { data } = await api.get(`/admin/orders/${o.id}/jubelio/status`);
      setJubStatus(data);
      toast.success(`Status: ${data?.tracking_status || data?.sync?.status || "tersinkron"}`);
      // Phase 4: sync UI tracking panel + order detail
      await fetchTracking();
      await load();
    } catch (e) { notifyError(e); }
    finally { setJubBusy(false); fetchJubLog(); }
  };

  const cancelJubShipment = async () => {
    const reason = (cancelReason || "").trim();
    if (reason.length < 3) {
      toast.error("Alasan pembatalan wajib diisi (minimal 3 karakter)");
      return;
    }
    setCancelBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/jubelio/cancel`, { reason });
      toast.success(`AWB ${data?.awb || o.awb || ""} berhasil dibatalkan`);
      setCancelOpen(false);
      setCancelReason("");
      // Refresh tracking panel + order detail langsung
      await fetchTracking();
      await load();
      fetchJubLog();
    } catch (e) {
      notifyError(e);
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <AdminLayout>
      <Link to="/admin/pesanan" className="text-sm text-[#5C5C5C] hover:text-[#E85D04]">← Kembali</Link>
      <div className="flex items-center justify-between mt-2 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black">{o.order_number}</h1>
          <p className="text-sm text-[#5C5C5C]">{new Date(o.created_at).toLocaleString("id-ID")}</p>
        </div>
        <Button variant="outline" onClick={printInvoice} className="rounded-xl"><Printer size={14} className="mr-1" /> Cetak Invoice</Button>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4 mt-6">
        <div className="space-y-4">
          <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <h3 className="font-display font-bold mb-3">Item Pesanan</h3>
            <div className="space-y-3">
              {o.items.map((it, i) => (
                <div key={`${it.product_id}-${i}`} className="flex gap-3 pb-3 border-b border-[#E5E5E5] last:border-0 last:pb-0">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#FAF9F6]">
                    {it.image && <img src={imageUrl(it.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{it.name}</div>
                    <div className="text-xs text-[#5C5C5C]">{it.quantity} {it.unit} × {formatRp(it.unit_price)}</div>
                  </div>
                  <div className="font-bold">{formatRp(it.subtotal)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#E5E5E5] space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#5C5C5C]">Subtotal</span><span>{formatRp(o.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C5C5C]">Ongkir ({o.shipping_method_name})</span><span>{formatRp(o.shipping_cost)}</span></div>
              {o.discount > 0 && <div className="flex justify-between text-[#2D6A4F]"><span>Diskon</span><span>-{formatRp(o.discount)}</span></div>}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-[#E5E5E5]"><span>Total</span><span className="text-[#E85D04]">{formatRp(o.total)}</span></div>
            </div>
          </section>

          <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <h3 className="font-display font-bold mb-2">Pelanggan & Pengiriman</h3>
            <div className="text-sm space-y-1">
              <div><b>{o.user_name}</b> · {o.user_email}</div>
              <div className="text-[#5C5C5C]">{o.address_snapshot?.recipient_name} · {o.address_snapshot?.phone}</div>
              <div className="text-[#5C5C5C]">{o.address_snapshot?.address}, {o.address_snapshot?.city} {o.address_snapshot?.postal_code}</div>
              {o.notes && <div className="mt-2"><b>Catatan:</b> {o.notes}</div>}
            </div>
          </section>

          {o.payment_method === "midtrans" && o.midtrans && (
            <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="admin-midtrans-info">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display font-bold">Pembayaran Online — Midtrans</h3>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                  o.payment_status === "paid" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]"
                  : o.payment_status === "pending" ? "bg-[#FFB703]/15 text-[#B5790F]"
                  : o.payment_status === "failed" ? "bg-red-100 text-red-700"
                  : o.payment_status === "expired" ? "bg-gray-200 text-gray-700"
                  : o.payment_status === "cancelled" ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
                }`} data-testid="midtrans-payment-status">
                  {o.payment_status === "paid" ? "Paid"
                    : o.payment_status === "pending" ? "Pending"
                    : o.payment_status === "failed" ? "Failed"
                    : o.payment_status === "expired" ? "Expired"
                    : o.payment_status === "cancelled" ? "Cancelled"
                    : (o.payment_status || "Created")}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Metode</div>
                  <div className="font-bold">Midtrans Snap</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Tipe Pembayaran</div>
                  <div className="font-mono text-xs break-all">{o.midtrans.payment_type || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Transaction ID</div>
                  <div className="font-mono text-xs break-all">{o.midtrans.transaction_id || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Order ID Midtrans</div>
                  <div className="font-mono text-xs break-all">{o.midtrans.order_id || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Gross Amount</div>
                  <div className="font-bold text-[#E85D04]">{formatRp(o.midtrans.gross_amount || o.total)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Paid At</div>
                  <div>{o.paid_at ? new Date(o.paid_at).toLocaleString("id-ID") : "—"}</div>
                </div>
              </div>
              {o.payment_status === "paid" && (
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800">
                  ✓ Pembayaran terverifikasi otomatis via webhook Midtrans. Tidak perlu upload/verifikasi bukti manual.
                </div>
              )}
              {o.payment_status !== "paid" && o.payment_status !== "expired" && o.payment_status !== "cancelled" && (
                <div className="mt-3">
                  <Button
                    onClick={async () => {
                      try {
                        const r = await api.post(`/admin/orders/${o.id}/midtrans/check-status`, {});
                        toast.success(`Status terbaru: ${r.data.transaction_status || "?"} → ${r.data.payment_status || "?"}`);
                        load?.();
                      } catch (e) { notifyError(e); }
                    }}
                    variant="outline"
                    className="rounded-xl border-[#E85D04] text-[#E85D04] hover:bg-[#FFEDD5]"
                    data-testid="admin-midtrans-check-status-btn"
                  >
                    Cek Status Midtrans
                  </Button>
                  <div className="text-[10px] text-[#5C5C5C] mt-1">
                    Fallback kalau webhook gagal/lambat — query langsung ke Midtrans Status API.
                  </div>
                </div>
              )}
              {o.midtrans.webhook_events?.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-[#5C5C5C] hover:text-[#E85D04] font-bold">Riwayat Webhook ({o.midtrans.webhook_events.length})</summary>
                  <div className="mt-2 space-y-1 max-h-48 overflow-auto">
                    {o.midtrans.webhook_events.slice().reverse().map((ev, i) => (
                      <div key={`mt-evt-${i}`} className="bg-[#FAF9F6] rounded p-2 font-mono">
                        <div>{ev.at} · <b>{ev.transaction_status}</b>{ev.fraud_status ? ` (${ev.fraud_status})` : ""}</div>
                        {ev.payment_type && <div className="text-[#5C5C5C]">type: {ev.payment_type}</div>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </section>
          )}

          {o.payment_proof_path && (
            <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display font-bold">Verifikasi Pembayaran</h3>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                  o.payment_status === "verified" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]"
                  : o.payment_status === "rejected" ? "bg-red-100 text-red-700"
                  : "bg-[#FFB703]/15 text-[#B5790F]"
                }`}>
                  {o.payment_status === "verified" ? "Diverifikasi" : o.payment_status === "rejected" ? "Ditolak" : "Menunggu Verifikasi"}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C] mb-2">Metode Pembayaran</div>
                  <div className="font-bold uppercase">{o.payment_method}</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">Total: <b className="text-[#E85D04]">{formatRp(o.total)}</b></div>
                  {isQris && settings?.qris_image && (
                    <div className="mt-3">
                      <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C] mb-1">QRIS yang Digunakan</div>
                      <img src={imageUrl(settings.qris_image)} alt="QRIS" className="w-32 h-32 object-contain bg-white border border-[#E5E5E5] rounded-lg p-2" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C] mb-2">Bukti Pembayaran Pelanggan</div>
                  <a href={imageUrl(o.payment_proof_path)} target="_blank" rel="noreferrer">
                    <img src={imageUrl(o.payment_proof_path)} alt="Bukti" className="max-h-64 rounded-xl border border-[#E5E5E5] hover:opacity-90 transition-opacity" data-testid="admin-payment-proof-img" />
                  </a>
                  {o.payment_rejection_reason && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                      <b>Alasan ditolak sebelumnya:</b> {o.payment_rejection_reason}
                    </div>
                  )}
                </div>
              </div>

              {o.payment_status !== "verified" && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#E5E5E5]">
                  <Button
                    onClick={verifyPayment}
                    disabled={busy}
                    className="bg-[#2D6A4F] hover:bg-[#1f4d3a] rounded-xl font-bold"
                    data-testid="verify-payment-btn"
                  >
                    <CheckCircle2 size={16} className="mr-1" /> Verifikasi Pembayaran
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRejectOpen(true)}
                    disabled={busy}
                    className="rounded-xl text-[#DC2F02] border-[#DC2F02] hover:bg-red-50 hover:text-[#DC2F02]"
                    data-testid="reject-payment-btn"
                  >
                    <XCircle size={16} className="mr-1" /> Tolak Pembayaran
                  </Button>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="bg-white border border-[#E5E5E5] rounded-2xl p-5 h-fit">
          <h3 className="font-display font-bold mb-3">Update Status</h3>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs uppercase font-bold tracking-widest">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest">No Resi</label>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Nomor resi pengiriman" className="rounded-xl" data-testid="admin-tracking-input" />
            </div>
            <Button onClick={updateStatus} className="w-full brand-btn-primary rounded-xl" data-testid="admin-update-status-btn">Simpan</Button>
          </div>
        </aside>
      </div>

      {(jubelioActive || hasAwb) && (
        <div className="mt-4 grid lg:grid-cols-[1fr_320px] gap-4">
          <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="jubelio-order-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-display font-bold">
                Jubelio Shipment <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFEDD5] text-[#E85D04] ml-1">v1.8</span>
                {jubelio?.sandbox && <span className="text-xs uppercase font-bold px-2 py-1 rounded bg-amber-100 text-amber-700 ml-2">Sandbox</span>}
              </h3>
              {hasAwb ? (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-100 text-emerald-700" data-testid="jubelio-status-active">AWB Aktif</span>
              ) : o.jubelio_dry_run_payload ? (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-700" data-testid="jubelio-status-dryrun">Dry Run Preview</span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#F0E5C0] text-[#7A5440]" data-testid="jubelio-status-none">Belum dibuat</span>
              )}
            </div>

            {hasAwb ? (
              <div className="mt-3 text-sm space-y-1" data-testid="jubelio-awb-info">
                <div className="flex items-center gap-2 flex-wrap">
                  <b>AWB / Resi:</b>
                  <span className="font-mono text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5" data-testid="awb-value">{o.awb || o.tracking_number}</span>
                  <button
                    onClick={copyAwb}
                    type="button"
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FFEDD5] text-[#E85D04] hover:bg-[#FFD7A8] transition-colors"
                    data-testid="copy-awb-btn"
                    title="Salin nomor AWB"
                  >
                    Salin
                  </button>
                </div>
                <div><b>Shipment ID:</b> <span className="font-mono text-xs">{o.jubelio_shipment_id || "-"}</span></div>
                {o.tracking_url && (
                  <div><b>Tracking URL:</b> <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[#E85D04] underline break-all" data-testid="tracking-url">{o.tracking_url}</a></div>
                )}
                <div><b>Kurir:</b> {o.courier_name || o.jubelio_courier || "-"} {o.courier_service && <span className="text-[#5C5C5C]">· {o.courier_service}</span>}</div>
                {o.jubelio_price != null && <div><b>Harga (Jubelio):</b> {formatRp(o.jubelio_price)}</div>}
                <div><b>Status Pengiriman:</b> <span className="font-mono text-xs uppercase">{o.shipping_status || "-"}</span></div>
                {o.jubelio_cancelled && (
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                    Shipment ini sudah dibatalkan. Alasan: {o.jubelio_cancel_reason || "-"}
                  </div>
                )}
                {jubStatus && (
                  <div className="mt-3 bg-[#FAF9F6] border border-[#E5E5E5] rounded-lg p-3 text-xs" data-testid="jubelio-status-result">
                    <div><b>Status:</b> {jubStatus.status}</div>
                    {Array.isArray(jubStatus.history) && jubStatus.history.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {jubStatus.history.map((h, i) => (
                          <li key={i} className="border-l-2 border-[#E85D04] pl-2">
                            <div className="font-semibold">{h.status}</div>
                            <div className="text-[#5C5C5C]">{h.note} · {h.time && new Date(h.time).toLocaleString("id-ID")}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button onClick={fetchJubStatus} disabled={jubBusy} variant="outline" className="rounded-xl" data-testid="jubelio-check-status-btn">Cek Status</Button>
                  {/* Phase 5: Batalkan AWB — hanya muncul kalau:
                     - punya AWB
                     - belum delivered
                     - belum cancelled (di tracking ATAU di flag jubelio_cancelled) */}
                  {(o.awb || o.tracking_number) &&
                    (o.tracking_status || o.shipping_status) !== "delivered" &&
                    (o.tracking_status || o.shipping_status) !== "cancelled" &&
                    !o.jubelio_cancelled && (
                    <Button
                      onClick={() => { setCancelReason(""); setCancelOpen(true); }}
                      disabled={cancelBusy}
                      className="rounded-xl bg-[#DC2F02] hover:bg-[#b02701] text-white"
                      data-testid="jubelio-cancel-awb-btn"
                    >
                      Batalkan AWB
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {o.jubelio_dry_run_payload && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs" data-testid="dry-run-preview">
                    <div className="font-bold text-amber-900 mb-1">Preview Payload Tersimpan (Dry Run)</div>
                    <div className="text-amber-800">
                      ref_no: <b>{o.jubelio_dry_run_payload.ref_no}</b> · courier_id: <b>{o.jubelio_dry_run_payload.courier_id}</b> · service_id: <b>{o.jubelio_dry_run_payload.courier_service_id}</b>
                    </div>
                    <details className="mt-2 cursor-pointer">
                      <summary className="text-amber-900 hover:underline">Lihat JSON lengkap</summary>
                      <pre className="mt-1 text-[10px] bg-white p-2 rounded border border-amber-200 overflow-x-auto">{JSON.stringify(o.jubelio_dry_run_payload, null, 2)}</pre>
                    </details>
                  </div>
                )}
                <p className="text-sm text-[#5C5C5C]">
                  Generate AWB melalui Jubelio Shipment v1.8. <b>Default: dry_run</b> (preview payload tanpa hit API). Untuk membuat AWB nyata, aktifkan toggle <code>enable_create_awb</code> di Admin → Jubelio.
                </p>
                {o.provider_used && o.provider_used !== "jubelio" && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800" data-testid="awb-not-eligible">
                    <b>Order tidak eligible Generate AWB.</b> Rate diambil dari fallback <code>{o.provider_used}</code> (mock/manual), tidak punya courier_id Jubelio. Customer perlu checkout ulang saat provider Jubelio aktif untuk dapat AWB nyata.
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => { setAwbDryRun(true); setAwbResult(null); setAwbConfirmOpen(true); }}
                    disabled={jubBusy || (o.provider_used && o.provider_used !== "jubelio")}
                    className="bg-[#4A2E1F] hover:bg-[#3a2417] rounded-xl text-white disabled:opacity-50"
                    data-testid="jubelio-create-shipment-btn"
                  >
                    {jubBusy ? "Memproses..." : "Generate AWB"}
                  </Button>
                  {!enableCreateAwb && (
                    <span className="text-[10px] text-[#5C5C5C]" data-testid="awb-gate-disabled-hint">Gate AWB nyata: OFF (dry_run only)</span>
                  )}
                </div>
              </div>
            )}

            {/* Phase 4: Tracking Pengiriman — hanya muncul kalau ada AWB */}
            {hasAwb && (
              <div className="mt-4 border-t border-[#E5E5E5] pt-3" data-testid="admin-tracking-section">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#7A5440] mb-3 flex items-center gap-2">
                  <Search size={12} /> Tracking Pengiriman
                </h3>
                <TrackingPanel
                  data={trackingDetail || {
                    awb: o.awb || o.tracking_number,
                    tracking_url: o.tracking_url,
                    courier_name: o.courier_name,
                    courier_code: o.courier_code,
                    courier_service: o.courier_service,
                    tracking_status: o.tracking_status,
                    tracking_events: o.tracking_events || [],
                    tracking_last_sync: o.tracking_last_sync,
                    tracking_sync_error: o.tracking_sync_error,
                    tracking_response_time_ms: o.tracking_response_time_ms,
                  }}
                  onRefresh={refreshTracking}
                  role="admin"
                />
              </div>
            )}

            {/* Inspect Last Response — collapsible debug panel */}
            <div className="mt-4 border-t border-[#E5E5E5] pt-3">
              <button
                type="button"
                onClick={() => { setJubLogOpen(!jubLogOpen); if (!jubLog) fetchJubLog(); }}
                className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#7A5440] hover:text-[#4A2E1F]"
                data-testid="jubelio-log-toggle"
              >
                <span className="flex items-center gap-2">
                  <Search size={12} /> Inspect Last Response
                  {jubLog && !jubLog.empty && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                      jubLog.status_code >= 200 && jubLog.status_code < 300 ? "bg-emerald-100 text-emerald-700"
                      : jubLog.status_code >= 400 ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-700"
                    }`}>{jubLog.status_code || "ERR"}</span>
                  )}
                </span>
                <span className="text-[#5C5C5C]">{jubLogOpen ? "▲" : "▼"}</span>
              </button>
              {jubLogOpen && (
                <div className="mt-3 bg-[#1a1a1a] text-[#e5e5e5] rounded-lg p-3 text-xs font-mono space-y-1 overflow-x-auto" data-testid="jubelio-log-panel">
                  {!jubLog || jubLog.empty ? (
                    <div className="text-[#888]">Belum ada panggilan Jubelio untuk order ini.</div>
                  ) : (
                    <>
                      <div><span className="text-[#888]">METHOD:</span> <span className="text-[#fbbf24]">{jubLog.method}</span></div>
                      <div className="break-all"><span className="text-[#888]">ENDPOINT:</span> <span className="text-[#60a5fa]">{jubLog.endpoint}</span></div>
                      <div><span className="text-[#888]">STATUS:</span> <span className={jubLog.status_code >= 400 ? "text-[#f87171]" : "text-[#34d399]"}>{jubLog.status_code || "—"}</span></div>
                      {jubLog.at && <div><span className="text-[#888]">AT:</span> {new Date(jubLog.at).toLocaleString("id-ID")}</div>}
                      {jubLog.error && (
                        <div><span className="text-[#888]">ERROR:</span> <span className="text-[#f87171] break-words">{jubLog.error}</span></div>
                      )}
                      {jubLog.response_snippet && (
                        <details className="mt-2">
                          <summary className="text-[#888] cursor-pointer hover:text-[#e5e5e5]">RESPONSE BODY (max 500 chars, sudah di-redact)</summary>
                          <pre className="mt-1 whitespace-pre-wrap break-words text-[#e5e5e5]">{jubLog.response_snippet}</pre>
                        </details>
                      )}
                      <div className="mt-2 pt-2 border-t border-[#333] text-[10px] text-[#666]">
                        🔒 Token, Client Secret, dan Authorization header tidak pernah disimpan / ditampilkan di sini.
                      </div>
                    </>
                  )}
                  <button onClick={fetchJubLog} className="mt-2 text-[10px] text-[#60a5fa] hover:underline" data-testid="jubelio-log-refresh">Refresh</button>
                </div>
              )}
            </div>

            {/* Audit Logs — riwayat Generate AWB (dry_run + real) untuk order ini */}
            <div className="mt-3 border-t border-[#E5E5E5] pt-3">
              <button
                type="button"
                onClick={() => { const next = !auditOpen; setAuditOpen(next); if (next) fetchAuditLogs(); }}
                className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#7A5440] hover:text-[#4A2E1F]"
                data-testid="jubelio-audit-toggle"
              >
                <span className="flex items-center gap-2">
                  <Search size={12} /> Audit Log Generate AWB
                  {auditLogs.length > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-[#FFEDD5] text-[#E85D04]">{auditLogs.length}</span>
                  )}
                </span>
                <span className="text-[#5C5C5C]">{auditOpen ? "▲" : "▼"}</span>
              </button>
              {auditOpen && (
                <div className="mt-3 bg-[#FAF9F6] border border-[#E5E5E5] rounded-lg p-3 text-xs space-y-2 max-h-80 overflow-y-auto" data-testid="jubelio-audit-panel">
                  {auditBusy && <div className="text-[#5C5C5C]">Memuat...</div>}
                  {!auditBusy && auditLogs.length === 0 && (
                    <div className="text-[#5C5C5C]" data-testid="audit-empty">Belum ada audit log untuk order ini.</div>
                  )}
                  {auditLogs.map((row) => (
                    <div key={row.id} className="bg-white border border-[#E5E5E5] rounded-md p-2" data-testid="audit-row">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          row.success ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                        }`} data-testid={`audit-status-${row.success ? "ok" : "fail"}`}>
                          {row.success ? "OK" : "FAIL"}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          row.dry_run ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                        }`}>
                          {row.dry_run ? "DRY RUN" : "REAL"}
                        </span>
                        <span className="text-[10px] text-[#5C5C5C]">{row.created_at && new Date(row.created_at).toLocaleString("id-ID")}</span>
                        {typeof row.duration_ms === "number" && (
                          <span className="text-[10px] text-[#5C5C5C]">· {row.duration_ms} ms</span>
                        )}
                        {typeof row.attempts === "number" && row.attempts > 1 && (
                          <span className="text-[10px] text-[#DC2F02] font-bold">· {row.attempts}× retry</span>
                        )}
                      </div>
                      <div className="mt-1 text-[#5C5C5C] text-[11px]">
                        Aktor: <b className="text-[#1a1a1a]">{row.actor || "—"}</b>
                      </div>
                      {row.error && (
                        <div className="mt-1 text-red-700 text-[11px] break-words">
                          <b>Error</b> {row.error_code ? `[${row.error_code}]` : ""}: {row.error}
                        </div>
                      )}
                      {row.response?.awb && (
                        <div className="mt-1 text-[11px]">
                          <b>AWB:</b> <span className="font-mono">{row.response.awb}</span>
                        </div>
                      )}
                      {(row.payload || row.response) && (
                        <details className="mt-1">
                          <summary className="text-[10px] cursor-pointer text-[#5C5C5C] hover:text-[#1a1a1a]">JSON detail</summary>
                          <pre className="mt-1 text-[10px] bg-[#1a1a1a] text-[#e5e5e5] p-2 rounded overflow-x-auto whitespace-pre-wrap break-words">{JSON.stringify({ payload: row.payload, response: row.response }, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                  <button onClick={fetchAuditLogs} className="mt-1 text-[10px] text-[#E85D04] hover:underline" data-testid="jubelio-audit-refresh">Refresh</button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {/* v1.8 Generate AWB confirmation dialog */}
      <Dialog open={awbConfirmOpen} onOpenChange={setAwbConfirmOpen}>
        <DialogContent data-testid="awb-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Generate AWB Jubelio</DialogTitle>
            <DialogDescription className="sr-only">Konfirmasi pembuatan resi (AWB) Jubelio dengan opsi dry_run atau produksi.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900" data-testid="awb-confirm-warning">
              <b>Apakah Anda yakin ingin membuat resi di Jubelio? Tindakan ini dapat membuat pengiriman nyata.</b>
            </div>
            <label className="flex items-start gap-2 p-3 rounded-lg border border-[#E5E5E5] hover:border-[#E85D04] cursor-pointer" data-testid="awb-mode-dry-run">
              <input type="radio" name="awb-mode" checked={awbDryRun} onChange={() => setAwbDryRun(true)} className="mt-1" data-testid="awb-mode-dry-run-radio" />
              <div className="flex-1">
                <div className="text-sm font-bold">Dry Run (Preview)</div>
                <div className="text-xs text-[#5C5C5C]">Hanya menyimpan preview payload + log. TIDAK menembak API Jubelio. <span className="text-[#2D6A4F] font-semibold">Aman & disarankan.</span></div>
              </div>
            </label>
            <label className={`flex items-start gap-2 p-3 rounded-lg border ${enableCreateAwb ? "border-[#E5E5E5] hover:border-[#DC2F02]" : "border-[#E5E5E5] bg-[#FAF9F6] opacity-60 cursor-not-allowed"}`} data-testid="awb-mode-real">
              <input
                type="radio"
                name="awb-mode"
                checked={!awbDryRun}
                onChange={() => setAwbDryRun(false)}
                disabled={!enableCreateAwb}
                className="mt-1"
                data-testid="awb-mode-real-radio"
              />
              <div className="flex-1">
                <div className="text-sm font-bold text-[#DC2F02]">Generate AWB Nyata (Production)</div>
                <div className="text-xs text-[#5C5C5C]">Memanggil <code>POST /shipments/create</code> ke Jubelio dan menghasilkan AWB resmi.</div>
                {!enableCreateAwb && <div className="text-[10px] text-amber-700 mt-1">⚠ Disabled. Aktifkan toggle <b>&quot;Enable Generate AWB Production&quot;</b> di Admin → Jubelio Settings.</div>}
              </div>
            </label>
            {awbResult && (
              <div className="bg-[#1a1a1a] text-[#e5e5e5] rounded-lg p-3 text-xs font-mono max-h-40 overflow-y-auto" data-testid="awb-result-preview">
                <div className="text-[#60a5fa] mb-1">{awbResult.dry_run ? "PREVIEW PAYLOAD" : "RESPONSE JUBELIO"}</div>
                <pre className="whitespace-pre-wrap break-words">{JSON.stringify(awbResult.payload || awbResult, null, 2).slice(0, 1200)}</pre>
              </div>
            )}
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button variant="outline" onClick={() => setAwbConfirmOpen(false)} disabled={jubBusy} className="rounded-xl" data-testid="awb-cancel-btn">Batal</Button>
            <Button
              onClick={runGenerateAwb}
              disabled={jubBusy || (!awbDryRun && !enableCreateAwb)}
              className={awbDryRun ? "bg-[#4A2E1F] hover:bg-[#3a2417] rounded-xl text-white" : "bg-[#DC2F02] hover:bg-[#b02701] rounded-xl text-white"}
              data-testid="awb-confirm-btn"
            >
              {jubBusy ? "Memproses..." : (awbDryRun ? "Generate Dry Run" : "Generate AWB Nyata")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tolak Pembayaran</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#5C5C5C] mb-2">Tulis alasan penolakan. Pesan ini akan ditampilkan ke pelanggan agar mereka bisa upload ulang.</div>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Contoh: Nominal transfer tidak sesuai dengan total pesanan"
            rows={3}
            data-testid="reject-reason-input"
          />
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy} className="rounded-xl">Batal</Button>
            <Button onClick={rejectPayment} disabled={busy || !rejectReason.trim()} className="bg-[#DC2F02] hover:bg-[#b02701] rounded-xl" data-testid="reject-payment-confirm">
              {busy ? "Memproses..." : "Tolak Pembayaran"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 5: Batalkan AWB modal */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-awb-dialog">
          <DialogHeader>
            <DialogTitle>Batalkan AWB Jubelio</DialogTitle>
            <DialogDescription className="sr-only">
              Konfirmasi pembatalan resi AWB di Jubelio. Tindakan ini akan
              mengirim permintaan cancel ke kurir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="cancel-awb-warning">
              ⚠ <b>Perhatian:</b> Permintaan cancel akan dikirim ke Jubelio dan
              kurir terkait ({o.courier_name || o.courier_code || "-"}). Cancel
              dapat <b>ditolak</b> oleh kurir bila paket sudah diproses fisik
              (sudah pickup / sudah masuk hub). Jika ditolak, AWB tetap aktif.
            </div>
            <div className="text-sm">
              <div className="text-[#7A5440] uppercase tracking-wider font-semibold text-[10px]">AWB / Resi</div>
              <div className="font-mono bg-[#FAF9F6] border border-[#E5E5E5] rounded px-2 py-1 mt-0.5 inline-block">{o.awb || o.tracking_number || "-"}</div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#7A5440]">
                Alasan Pembatalan <span className="text-red-600">*</span>
              </label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Contoh: Barang belum siap dikirim / Pesanan customer dibatalkan / Stok habis"
                rows={3}
                className="mt-1"
                data-testid="cancel-reason-input"
                disabled={cancelBusy}
              />
              <div className="text-[10px] text-[#5C5C5C] mt-1">
                Minimal 3 karakter. Alasan akan disimpan di audit log Jubelio.
              </div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelBusy}
              className="rounded-xl"
              data-testid="cancel-awb-dismiss"
            >
              Batal
            </Button>
            <Button
              onClick={cancelJubShipment}
              disabled={cancelBusy || cancelReason.trim().length < 3}
              className="bg-[#DC2F02] hover:bg-[#b02701] rounded-xl text-white"
              data-testid="cancel-awb-confirm"
            >
              {cancelBusy ? "Memproses..." : "Konfirmasi Batalkan AWB"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
